import React from 'react';
import { useTranslation } from 'react-i18next';
import { useSelector } from 'react-redux';
import { Alert, Button, Flex, Typography } from 'antd';
import FadeIn from '../FadeIn';
import DocumentSlots from '../document-slots/DocumentSlots';
import useApiClient from '../../hooks/api/useApiClient';
import useBusinessProfile from '../../hooks/context/useBusinessProfile';
import usePermissions from '../../hooks/auth/usePermissions';
import { BusinessProfileReaderRoles } from '../../services/permissions/PermissionResolver';
import useHistory from '../../hooks/navigation/useHistory';
import { ScreenEnum } from '../../hooks/navigation/Screens';
import { countMissingSlots } from '../document-slots/documentFiles';
import { useGetDocumentSlotsQuery } from '../../store/api-slices';

/**
 * Get Started's Upload documents step for restaurants in Germany (PROD-544), after contract
 * payment. Every upload is stored right away, so Next only moves on. A missing required document
 * never blocks the owner (Nishita): they can continue, and the step stays open, marked Missing,
 * until it is uploaded. When no later step is open, there is nothing to move on to, so the
 * button takes the owner home instead of doing nothing. A file uploaded by mistake can be
 * removed while the backend allows it, which it says with the slots: until onboarding is
 * complete for every restaurant sharing the profile, and none is live (Bohdan). A restaurant
 * that went through the old document steps owes the slots nothing, so nothing is missing for it.
 */
export default function DocumentsStep({ moveBackwards, moveForward, hasNextStep = true }) {
  const { t } = useTranslation(undefined, { keyPrefix: 'Components:Verification:DocumentsStep' });

  const history = useHistory();
  const { company } = useSelector((state) => state.currentCompany);
  const { getValue } = useBusinessProfile();
  const { hasAnyRole } = usePermissions();
  const { BusinessProfilesManager } = useApiClient();

  // The same query the cards and useVerification read, so this is the cached answer.
  const { currentData: slots } = useGetDocumentSlotsQuery(
    {
      apiManager: BusinessProfilesManager,
      companyId: company?.id,
      legalForm: getValue('legal_form') ?? null,
    },
    { skip: !hasAnyRole(BusinessProfileReaderRoles) },
  );
  const missing = company?.merchant?.requires_document_slots === false ? 0 : countMissingSlots(slots);
  const canRemove = Boolean(slots?.[0]?.removable);

  return (
    <FadeIn>
      <Typography.Paragraph>{t('description')}</Typography.Paragraph>
      <DocumentSlots canRemove={canRemove} />
      {missing > 0 && <Alert type="warning" showIcon className="mt-md" title={t('missing', { count: missing })} />}
      <Flex justify="end" wrap gap={8} className="mt-lg">
        {moveBackwards && <Button onClick={moveBackwards}>{t('buttons.back')}</Button>}
        {hasNextStep ? (
          <Button type="primary" onClick={moveForward} data-testid="documents-step-next">
            {t('buttons.next')}
          </Button>
        ) : (
          <Button
            type="primary"
            onClick={() => history.push(ScreenEnum.Home)}
            data-testid="documents-step-finish-later"
          >
            {t('buttons.finish_later')}
          </Button>
        )}
      </Flex>
    </FadeIn>
  );
}
